/**
 * API client for same-domain communication with the Pharmacy Clinical Copilot backend.
 * Uses /api/* paths through CloudFront routing to ALB.
 * Stage-agnostic - works with any environment through relative URLs.
 */

const API_BASE_URL = '/api';

export interface UserInfo {
  firstName?: string;
  lastName?: string;
  email?: string;
  username?: string;
  posixGroups: string[];
  cognitoGroups: string[];
  userId?: string;
  provider?: string;
  authenticated: boolean;
  headers: Record<string, string>;
  cookies: Record<string, string>;
  timestamp: string;
}

export interface WebSocketCredentials {
  accessKeyId: string;
  secretAccessKey: string;
  sessionToken: string;
  websocketUrl: string;
  region: string;
}

export interface FeedbackData {
  session_id: string | null;
  prompt: string;
  response: string;
  is_helpful: boolean;
  notes?: string | null;
  not_helpful_reason?: string | null;
}

export interface FeedbackResponse {
  success: boolean;
  message?: string;
}

export const api = {
  /**
   * Get current user information from the /me endpoint.
   * Returns user info if authenticated, or unauthenticated state if not.
   */
  async getMe(): Promise<UserInfo | null> {
    try {
      const response = await fetch(`${API_BASE_URL}/me`, {
        credentials: 'include', // Ensure cookies are sent for authentication
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          console.log('User not authenticated - login required');
          return {
            authenticated: false,
            firstName: undefined,
            lastName: undefined,
            email: undefined,
            username: undefined,
            posixGroups: [],
            cognitoGroups: [],
            userId: undefined,
            provider: undefined,
            headers: {},
            cookies: {},
            timestamp: new Date().toISOString()
          };
        }
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const userData = await response.json();
      console.log('User authenticated:', userData.username || userData.email);
      return userData;
    } catch (error) {
      console.error('Error fetching user info:', error);
      // Return unauthenticated state for any error
      return {
        authenticated: false,
        firstName: undefined,
        lastName: undefined,
        email: undefined,
        username: undefined,
        posixGroups: [],
        cognitoGroups: [],
        userId: undefined,
        provider: undefined,
        headers: {},
        cookies: {},
        timestamp: new Date().toISOString()
      };
    }
  },

  async saveFeedback(feedbackData: FeedbackData): Promise<Response | null> {
    try {
      const response = await fetch(`${API_BASE_URL}/saveFeedback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(feedbackData)
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      return response;
    } catch (error) {
      console.error('Error in saveFeedback API:', error);
      return null;
    }
  },

  async summarizeCustomerPreviousInteraction(contact_id: string): Promise<Response | null> {
    try {
      const response = await fetch(`${API_BASE_URL}/summarizeCustomerPreviousInteraction`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'text/event-stream',
        },
        credentials: 'include',
        body: JSON.stringify({ contact_id })
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      return response;
    } catch (error) {
      console.error('Error in summarizeCustomerPreviousInteraction API:', error);
      return null;
    }
  },

  async getWebSocketCredentials(): Promise<WebSocketCredentials | null> {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/websocket-credentials`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error getting websocket credentials:', error);
      return null;
    }
  }
};
